/**
 * yardSaveTimerInstrumentation — iter386d / D1b-1
 *
 * Records one entry per autosave CYCLE.  A cycle starts at the
 * first `dirty()` after the last acknowledged save and ends at the
 * next PUT ack (or PUT error).  Between those two edges we record
 * every timer reset, tagged with the trigger source that caused it.
 *
 * No behaviour is changed.  All state transitions are observed
 * via callbacks the timer owner calls at existing decision points.
 *
 * Trigger sources:
 *   'local-edit'     — user changed the doc; timer (re)armed via markDirty
 *   'ws-echo'        — inbound WebSocket message reconciled state
 *   'remote-update'  — programmatic backend refetch reconciled state
 *   'view-toggle'    — view-mode toggle path (should NOT normally arm)
 *   'other'          — fallback
 */

const KEY = 'yard.save.cycles.v1';
const MAX = 30;

let _buffer = null;
let _active = null; // in-flight cycle

function _load() {
  if (_buffer) return _buffer;
  _buffer = [];
  if (typeof sessionStorage === 'undefined') return _buffer;
  try {
    const raw = sessionStorage.getItem(KEY);
    if (raw) {
      const p = JSON.parse(raw);
      if (Array.isArray(p)) _buffer = p.slice(-MAX);
    }
  } catch (_e) { /* corrupt — start fresh */ }
  return _buffer;
}

function _persist() {
  if (typeof sessionStorage === 'undefined') return;
  try { sessionStorage.setItem(KEY, JSON.stringify(_buffer.slice(-MAX))); }
  catch (_e) { /* quota */ }
}

/**
 * Called when the doc becomes dirty (via markDirty).  If a cycle
 * is already active, we count this as a reset; otherwise a new
 * cycle starts.
 */
export function markDirtyEvent(trigger = 'local-edit', meta = {}) {
  const now = Date.now();
  if (!_active) {
    _active = {
      firstDirtyAt: now,
      firstDirtyTrigger: trigger,
      resets: [],
      putDispatchAt: null,
      ackAt: null,
      errorAt: null,
      totalMs: null,
      resetCount: 0,
      meta,
    };
    return;
  }
  _active.resets.push({ at: now, trigger, meta });
  _active.resetCount += 1;
}

/** Called just before axios.put fires. */
export function markPutDispatch() {
  if (!_active) return;
  _active.putDispatchAt = Date.now();
}

/** Called when the PUT returns 2xx. */
export function markPutAck() {
  if (!_active) return;
  _active.ackAt = Date.now();
  _active.totalMs = _active.ackAt - _active.firstDirtyAt;
  _load().push(_active);
  if (_buffer.length > MAX) _buffer.splice(0, _buffer.length - MAX);
  _persist();
  _active = null;
}

/** Called when the PUT throws. */
export function markPutError(msg = 'save failed') {
  if (!_active) return;
  _active.errorAt = Date.now();
  _active.errorMessage = msg;
  _active.totalMs = _active.errorAt - _active.firstDirtyAt;
  _load().push(_active);
  if (_buffer.length > MAX) _buffer.splice(0, _buffer.length - MAX);
  _persist();
  _active = null;
}

export function getSaveCycles() { return _load().slice(); }
export function clearSaveCycles() { _buffer = []; _active = null; _persist(); }
export function serialiseSaveCycles() {
  return JSON.stringify({ inFlight: _active, completed: getSaveCycles() }, null, 2);
}
